import { styled } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import { ramps } from "../../../common/theme/brandTokens";

export const MarkdownBox = styled(Box)(({ theme }) => {
  const isDark = theme.palette.mode === "dark";
  const codeBg = isDark ? ramps.neutral[800] : ramps.neutral[100];
  const tableBorderColor = theme.palette.divider;
  const tableThBg = isDark ? ramps.neutral[800] : ramps.neutral[50];
  const tableEvenBg = isDark ? ramps.neutral[850] : ramps.neutral[25];
  const linkColor = theme.palette.primary.main;

  return {
    "& p": {
      marginBottom: "1em",
      lineHeight: 1.6,
      fontSize: "13px",
    },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      marginTop: "1.5em",
      marginBottom: "0.5em",
      fontWeight: 600,
      lineHeight: 1.3,
    },
    "& h1": {
      fontSize: "1.5em",
    },
    "& h2": {
      fontSize: "1.3em",
    },
    "& h3, & h4, & h5": {
      fontSize: "1.1em",
    },
    "& h6": {
      fontSize: "1.1em",
      lineHeight: 1.2,
      marginTop: 1,
    },
    "& table": {
      borderCollapse: "collapse",
      width: "100%",
      marginBottom: "1em",
      border: `1px solid ${tableBorderColor}`,
      overflow: "auto",
    },
    "& th": {
      backgroundColor: tableThBg,
      border: `1px solid ${tableBorderColor}`,
      padding: "4px 8px",
      textAlign: "left",
      fontWeight: 600,
      fontSize: "13px",
    },
    "& td": {
      border: `1px solid ${tableBorderColor}`,
      padding: "4px 8px",
      lineHeight: 1.3,
      fontSize: "13px",
    },
    "& tr:nth-of-type(even)": {
      backgroundColor: tableEvenBg,
    },
    "& img": {
      maxWidth: "100%",
      height: "auto",
      marginTop: "1em",
      marginBottom: "1em",
      borderRadius: "4px",
    },
    "& ul, & ol": {
      marginLeft: "1.2em",
      marginBottom: "0.5em",
      paddingLeft: "0.5em",
    },
    "& li": {
      marginBottom: "0.2em",
      lineHeight: 1.6,
      fontSize: "13px",
    },
    "& li p": {
      margin: 0,
      lineHeight: 1.6,
      fontSize: "13px",
    },
    "& code": {
      backgroundColor: codeBg,
      padding: "2px 6px",
      borderRadius: "3px",
      fontFamily: "monospace",
      fontSize: "0.9em",
    },
    "& pre": {
      backgroundColor: codeBg,
      padding: "13px",
      borderRadius: "4px",
      overflowX: "auto",
      marginBottom: "1em",
    },
    "& pre code": {
      backgroundColor: "transparent",
      padding: 0,
    },
    "& a": {
      color: linkColor,
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },
    "& br": {
      display: "block",
      content: '""',
      height: "0.5em",
    },
    "& sup": {
      fontSize: "0.8em",
      verticalAlign: "super",
    },
  };
});

export const ParagraphText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  "&:last-child": {
    marginBottom: 0,
  },
}));

export const Heading1Text = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
  fontWeight: 600,
}));

export const Heading2Text = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
  fontWeight: 600,
}));

export const Heading3Text = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  marginBottom: theme.spacing(0.5),
  fontWeight: 600,
}));

export const Heading4Text = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  marginBottom: theme.spacing(0.5),
  fontWeight: 600,
}));

export const Heading5Text = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  fontWeight: 600,
}));

export const Heading6Text = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  fontWeight: 600,
}));

export const UnorderedList = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
})) as typeof Typography;

export const OrderedList = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
})) as typeof Typography;

export const ListItemText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
})) as typeof Typography;

export const BlockQuoteText = styled(Typography)(({ theme }) => ({
  borderLeft: "4px solid",
  borderColor: theme.palette.primary.main,
  paddingLeft: theme.spacing(2),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  fontStyle: "italic",
  color: theme.palette.text.secondary,
})) as typeof Typography;

export const InlineCodeText = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
  paddingTop: theme.spacing(0.25),
  paddingBottom: theme.spacing(0.25),
  borderRadius: "10px",
  fontFamily: "monospace",
  fontSize: "0.875em",
})) as typeof Typography;

export const CodeBlockText = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  overflow: "auto",
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
})) as typeof Typography;

export const CodeBlockInner = styled("code")(() => ({
  fontFamily: "monospace",
  fontSize: "0.875rem",
}));

export const StrongText = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: "inherit",
  lineHeight: "inherit",
})) as typeof Typography;

export const EmText = styled(Typography)(() => ({
  fontStyle: "italic",
})) as typeof Typography;
